import { withTranslation } from 'react-i18next';
import DateRangePicker from 'react-bootstrap-daterangepicker';
import moment from 'moment';
import { Icon } from 'semantic-ui-react';
import {
  REPORT_ALL_TIME_START_DATE,
  REPORT_ALL_TIME_LABEL,
} from '../utils/constant';
import './styles.scss';

function DateRangeChart(props) {
  const {
    startDate,
    endDate,
    datePicker,
    preDefinedDateRanges = false,
    t,
    i18n,
  } = props;

  // Ensure the picker UI (which relies on moment) uses the active app language
  moment.locale(i18n?.language || 'en');

  // Helper to format visible text with Intl instead of moment
  const formatDisplayDate = (d) =>
    new Intl.DateTimeFormat(i18n?.language || 'en', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }).format(new Date(d));

  let { minDate = REPORT_ALL_TIME_START_DATE } = props;
  // Keep moment objects for the picker internals
  const minDateMoment = moment(minDate);

  const getCalendarYearTimestamps = () => {
    const startMonthIndex = 0; // January
    const endMonthIndex = 11; // December
    return {
      current: {
        start: moment().month(startMonthIndex).startOf('month'),
        end: moment().month(endMonthIndex).endOf('month'),
      },
      last: {
        start: moment()
          .subtract(1, 'year')
          .month(startMonthIndex)
          .startOf('month'),
        end: moment().subtract(1, 'year').month(endMonthIndex).endOf('month'),
      },
    };
  };

  const getFinancialYearTimestamps = () => {
    const startMonthIndex = 6; // July
    const endMonthIndex = 5; // June
    const currentMonth = new Date().getMonth();
    const inSecondHalfOfYear = currentMonth >= 6; // Date.getMonth() is 0-based

    return {
      current: {
        start: inSecondHalfOfYear
          ? moment().month(startMonthIndex).startOf('month')
          : moment()
              .subtract(1, 'year')
              .month(startMonthIndex)
              .startOf('month'),
        end: inSecondHalfOfYear
          ? moment().add(1, 'year').month(endMonthIndex).endOf('month')
          : moment().month(endMonthIndex).endOf('month'),
      },
      last: {
        start: inSecondHalfOfYear
          ? moment().subtract(1, 'year').month(startMonthIndex).startOf('month')
          : moment()
              .subtract(2, 'year')
              .month(startMonthIndex)
              .startOf('month'),
        end: inSecondHalfOfYear
          ? moment().month(endMonthIndex).endOf('month')
          : moment().subtract(1, 'year').month(endMonthIndex).endOf('month'),
      },
    };
  };

  const maxYear = new Date().getFullYear() + 5;
  // Avoid locale-specific parsing; build via components
  const maxDateMoment = moment([maxYear, 11, 31]); // Dec = 11

  const settings = {
    // The picker still needs a moment-style format. Month/day names will follow moment.locale above.
    locale: { format: 'DD-MMM-YYYY' },
    showDropdowns: true,
    minDate: minDateMoment,
    maxDate: maxDateMoment,
  };
  // `value` must always be set (even to '') so the input stays a controlled
  // element — leaving it out when startDate/endDate are cleared meant React
  // stopped managing the DOM value and the old dates stayed on screen.
  let exist = { value: '' };

  if (startDate && endDate) {
    // Visible value uses Intl (localized with i18n.language)
    exist.value = `${formatDisplayDate(startDate)} - ${formatDisplayDate(endDate)}`;

    // Internally the picker expects moment or Date; keep as-is if you pass moment/Date via props
    settings.startDate = startDate;
    settings.endDate = endDate;
  }

  let labelKeyMap = {};
  if (preDefinedDateRanges) {
    // bootstrap-daterangepicker hides the calendars behind a "Custom Range"
    // link by default whenever `ranges` is set — show both together instead
    settings.alwaysShowCalendars = true;

    // setup ranges by keys, and then convert into a "settings.ranges" mapping
    // where the labels are i18n-translated using these keys
    const rangeMap = {
      since_the_start: [minDateMoment, moment()],
      last_7_days: [moment().subtract(6, 'days'), moment()],
      last_28_days: [moment().subtract(27, 'days'), moment()],
      this_month: [moment().startOf('month'), moment().endOf('month')],
      last_month: [
        moment().subtract(1, 'month').startOf('month'),
        moment().subtract(1, 'month').endOf('month'),
      ],
      this_quarter: [moment().startOf('quarter'), moment()],
      last_quarter: [
        moment().startOf('quarter').subtract(3, 'month'),
        moment().startOf('quarter'),
      ],
      year_to_date: [moment().startOf('year'), moment()],
      this_financial_year: [
        getFinancialYearTimestamps().current.start,
        getFinancialYearTimestamps().current.end,
      ],
      last_financial_year: [
        getFinancialYearTimestamps().last.start,
        getFinancialYearTimestamps().last.end,
      ],
      this_calendar_year: [
        getCalendarYearTimestamps().current.start,
        getCalendarYearTimestamps().current.end,
      ],
      last_calendar_year: [
        getCalendarYearTimestamps().last.start,
        getCalendarYearTimestamps().last.end,
      ],
    };

    settings.ranges = Object.fromEntries(
      Object.entries(rangeMap).map(([key, val]) => {
        const label = t(`date_range.${key}`);
        labelKeyMap[label] = key;
        return [label, val];
      })
    );
  }

  // Wrap parent's handler so we can add a stable key
  const handleApply = (event, picker) => {
    const chosenKey = labelKeyMap[picker.chosenLabel] || null;
    // call parent’s function, but augment picker with our key
    datePicker(event, { ...picker, chosenKey });
  };

  return (
    <DateRangePicker initialSettings={settings} onApply={handleApply}>
      <div className="date-range-chart p-1">
        <input
          {...exist}
          placeholder={t('date_range.placeholder')}
          type="text"
          readOnly
          className="date-range-chart-date"
        />
        <Icon className="calendar" name="calendar alternate outline" />
      </div>
    </DateRangePicker>
  );
}

// Wrap component with withTranslation HOC
DateRangeChart = withTranslation()(DateRangeChart);

export default DateRangeChart;
